/**
 * Central registry of page help content.
 *
 * Each entry describes a page's purpose, what the user can do on it, how to
 * use it and any important notes. `PageHelp` looks an entry up by route (or an
 * explicit key) so pages only need to drop `<PageHelp />` next to their title.
 *
 * Keep entries specific to the actual feature set of the page — the content is
 * derived from the billing controllers/services and the page components.
 */
export interface PageHelpTranslation {
  /** Human-friendly page name shown in the modal header. */
  title: string;
  /** Optional one-line kicker under the title. */
  subtitle?: string;
  /** What this page/feature is and why it exists. */
  overview: string;
  /** Key capabilities available on the page. */
  features: string[];
  /** Step-by-step instructions for the main workflows. */
  steps: string[];
  /** Practical notes, warnings and gotchas. */
  tips?: string[];
}

export interface PageHelpContent extends PageHelpTranslation {
  /** Optional Bangla (বাংলা) translation shown via the language toggle. */
  bn?: PageHelpTranslation;
}

export const PAGE_HELP: Record<string, PageHelpContent> = {
  /* ------------------------------------------------------------------ */
  /* Billing — overview                                                  */
  /* ------------------------------------------------------------------ */
  '/billing': {
    title: 'Billing',
    subtitle: 'Consolidated financial overview',
    overview:
      'The Billing home gives you a consolidated financial snapshot of the hospital. It is generated from the same Top Sheet engine that powers the accountant report, pulling together billing invoices, diagnostic receipts, pharmacy sales, IPD advances, the cashbook, supplier/doctor payables, payroll, fixed assets and operating expenses into one screen. Income and expense cards follow the selected period; Patient Dues and Total Payables show the running all-time position.',
    features: [
      'Income cards for the selected period: Total Bill, Cash Collected and Due Collected (today vs old dues by IPD/OPD/Pharmacy).',
      'Expense cards for the selected period: Total Expenses, Referral Pay, Payroll, Operating Expenses and Fixed Assets.',
      'Outstanding cards for the running all-time position: Patient Dues and Total Payables.',
      'Click a card to expand its component boxes inside the card — no modal needed.',
      'The info (i) icon expands a bilingual (English / বাংলা) explanation inside the card.',
      'Quick action shortcuts to every billing sub-module.',
      'Income Trend chart for the last 7 days.',
      'Collections by Payment Mode donut chart (cash, card, UPI, bank transfer, insurance).',
    ],
    steps: [
      'Read the groups in order: Income → Expenses → Outstanding. Only Outstanding is all-time; the others use the selected period.',
      'Click a card value to see how it was built, then use the panel link to open the module that owns the figure.',
      'Use the quick action buttons to open Top Sheet, Invoices, Payments, Cashbook, Settlements, Assets or Expenses.',
      'Use the Income Trend and Payment Mode charts to spot patterns before drilling into a module.',
    ],
    tips: [
      'Total Bill is the same figure older reports called “net revenue”: gross minus discounts. Cash Collected is actual money received and can differ from the billed amount — open Due Collected to see how much of it settled today’s bills versus older dues.',
      'Patient Dues is the same figure older reports called “receivables”: unpaid patient bills, shown as a running all-time balance.',
      'Referral Pay is the only share expense paid to doctors and other registered referral partners (employee or outside agent). Only the unpaid part is a payable, so it appears under Total Payables and is never counted twice.',
      'Settlement values are read-only here; no accounting entry is created by viewing this page.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* Top Sheet                                                           */
  /* ------------------------------------------------------------------ */
  '/billing/top-sheet': {
    title: 'Top Sheet',
    subtitle: 'Read-only consolidated accounting report',
    overview:
      'The Top Sheet is a single accountant-friendly summary of the hospital’s financial position for a chosen period. It is derived on demand from the operational ledgers (invoices, payments, cashbook, diagnostic receipts, IPD advances, pharmacy sales, supplier payables, referral-partner shares and payroll). No double-entry book is created — it is a read-only management report, not a second source of truth.',
    features: [
      'Period filter with quick ranges. Admins and super admins also get From/To pickers and All Time; every other role is limited to the last 7 days.',
      'Headline KPIs including Total Bill, Cash Collected, Due Collected (today vs old dues), Total Expenses, Referral Pay, Net Profit, Patient Dues and Total Payables.',
      'Overview tab with income by source and shortcuts to every related module.',
      'Financials tab with collections by payment mode, Profit & Loss and outstanding position.',
      'Referrals tab breaking down referral-partner commission vs hospital share. Referral partners may be doctors, employees or outside agents.',
      'Print and Refresh actions.',
    ],
    steps: [
      'Click a quick range button. Admins and super admins can also pick an exact From/To range or clear it with All Time; other roles see at most the last 7 days.',
      'Read the KPI cards for the period total.',
      'Open the Overview tab to see gross/discount/net/collected/due per revenue source.',
      'Open the Financials tab for the P&L (Total Bill minus referral pay, payroll, assets and operating expenses) and the outstanding position.',
      'Open the Referrals tab to see referral-partner commission and settlement status.',
      'Use Refresh after new activity, or Print to produce a paper/PDF copy.',
    ],
    tips: [
      'The printed Top Sheet is an expense and collections report: it deliberately omits net profit and itemises expenses (including operating expenses by category) instead.',
      'IPD advances are collected separately and are not broken down by payment mode, so “Collections by Payment Mode” may not equal the total collected figure.',
      'Referral pay is an expense in the P&L, alongside payroll. Its unpaid portion is a payable — not a second expense.',
      'A doctor can be registered as a referral partner. Doctors earn only through this referral share, paid from the referral payout ledger (RP- vouchers).',
      'Patient Dues (invoices, diagnostics, pharmacy) are current outstanding bills, not period-limited, so they reflect the live position. Older reports call them “receivables”.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* Invoices                                                            */
  /* ------------------------------------------------------------------ */
  '/billing/invoices': {
    title: 'Invoices',
    subtitle: 'Patient billing invoices',
    overview:
      'Invoices are the patient-facing bills for services and items. An invoice is built from unbilled charge events captured elsewhere in the system (consultations, diagnostics, IPD, etc.), then carries a subtotal, tax, discount, total, paid amount, due amount and a payment status.',
    features: [
      'Search invoices by invoice number, patient name or mobile.',
      'Filter by status — pick Due to list every invoice that still has an outstanding balance — and by date range; the filters live in the URL so a filtered view can be bookmarked or shared.',
      'Create an invoice by selecting a patient/visit and pulling in unbilled charges.',
      'Record a payment directly against an invoice.',
      'Edit invoice line items, discount and tax.',
      'View an invoice and print a formatted bill/PDF.',
      'Bulk pay or bulk delete selected invoices.',
      'Paginated list with patient, totals, paid, due, status and date columns.',
    ],
    steps: [
      'Click Create Invoice, pick the patient and visit, select the unbilled charges to bill, then save.',
      'Find an invoice with the search box or by paging through the table.',
      'Click a patient name or the eye icon to open the invoice detail for review.',
      'Click the dollar icon on an unpaid/partial invoice to record a payment.',
      'The Record Payment window shows the invoice breakdown (subtotal, tax, discounts, total, paid, due) and lets you apply an invoice-level percentage or flat discount before collecting.',
      'Click the pencil icon to edit items, discount or tax.',
      'Select checkboxes to Pay Selected (bulk) or Delete Selected, then confirm.',
      'Use the print action inside the invoice view to generate the bill.',
    ],
    tips: [
      'Status moves through issued → partial → paid; a new invoice without payment stays issued.',
      'Only invoices with an outstanding balance offer the Record Payment action.',
      'Deleting an invoice is a destructive action and cannot be undone from this screen. Invoices that already have payments must have those payments deleted first.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* Payments                                                            */
  /* ------------------------------------------------------------------ */
  '/billing/payments': {
    title: 'Payments',
    subtitle: 'Collections and refunds',
    overview:
      'The Payments page is the ledger of every amount collected against invoices, plus refunds. Each payment records the invoice it settles, the patient, the amount, the payment method and the date.',
    features: [
      'Filter payments by date range.',
      'Summary of page collections and total number of payments.',
      'View a payment receipt / details.',
      'Process a refund with amount and reason against a payment.',
      'Bulk move selected payments to Trash; the badge shows how many are there.',
      'Restore trashed payments or delete them permanently.',
      'Paginated, method-labelled list.',
    ],
    steps: [
      'Set Date From / Date To to narrow the ledger, then Clear to reset.',
      'Click the eye icon to open the payment detail/receipt.',
      'Click the refund icon to issue a refund: enter the amount and a reason, then confirm.',
      'Select one or more rows and use Move to trash to remove them from collections, then confirm.',
      'Open Trash to restore a payment or delete it permanently.',
    ],
    tips: [
      'A trashed payment is excluded from every collection total and report until it is restored.',
      'Moving a payment to trash rebalances the linked invoice (its paid/due amounts) and the related cashbook entries.',
      'Restoring a payment re-posts its journal entry, rebuilds the cashbook day and rebalances the invoice.',
      'Refunds are recorded as negative movement against the original payment.',
      'Payments are normally created from the Invoices page; this page is for auditing, viewing and corrections.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* Cashbook                                                            */
  /* ------------------------------------------------------------------ */
  '/billing/cashbook': {
    title: 'Cashbook',
    subtitle: 'Daily cash movement and reconciliation',
    overview:
      'The Cashbook tracks the physical cash counter. Each shift opens with a starting float, accumulates cash-in and cash-out from collections and payments, and is closed by counting the drawer and reconciling the expected balance against the physical count.',
    features: [
      'Open Day with an opening balance and shift (MORNING / EVENING / NIGHT).',
      'Close Day with the counted closing balance and a variance reason.',
      'Summary cards for opening, cash-in, cash-out and net balance.',
      'Date-range filter.',
      'View individual cashbook entry details.',
      'Print the cashbook for a period.',
    ],
    steps: [
      'Click Open Day, enter the opening float and choose the shift, then save.',
      'Let collections and payments post entries during the shift.',
      'At the end of the shift click Close Day, enter the physically counted closing balance and select the shift.',
      'If the counted balance differs from the expected balance, provide a variance reason before saving.',
      'Use the date filter and Print for reconciliation reports.',
    ],
    tips: [
      'Open and close per shift so each counter session is reconciled separately.',
      'A variance reason is required whenever the closing count does not match the expected balance.',
      'The Cashbook Balance KPI on the Billing page is sourced from these entries.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* Fixed Assets                                                        */
  /* ------------------------------------------------------------------ */
  '/billing/assets': {
    title: 'Assets',
    subtitle: 'Fixed asset register',
    overview:
      'The Assets register records the fixed assets the hospital owns — medical equipment, furniture, vehicles and similar capital items. Each asset captures its category, purchase date, cost, status and, where relevant, the vendor invoice that funded it.',
    features: [
      'Summary of total asset cost, asset count, active assets and related payables.',
      'Add, edit and delete assets.',
      'Search the register.',
      'Status tracking: ACTIVE, UNDER_MAINTENANCE, DISPOSED, RETIRED.',
      'Asset categories shared with the Expenses section.',
    ],
    steps: [
      'Click Add Asset, fill in the name, category, purchase date, cost, status and location/department, then save.',
      'Use the search box to find an asset by name or code.',
      'Click the pencil icon to edit an asset, or the trash icon to delete it.',
      'Change the status to UNDER_MAINTENANCE, DISPOSED or RETIRED as the asset’s life changes.',
    ],
    tips: [
      'Asset purchase cost feeds the “Fixed Assets (Period)” figure on the Billing overview.',
      'Unpaid asset vendor invoices appear as Asset Vendor Payable on the Top Sheet.',
      'Manage the category list under Assets → Categories.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* Expenses                                                            */
  /* ------------------------------------------------------------------ */
  '/billing/expenses': {
    title: 'Operating Expenses',
    subtitle: 'Running costs and recurring expenses',
    overview:
      'Operating Expenses is the ledger of day-to-day running costs — utilities, rent, supplies, services and similar — as distinct from payroll and capital assets. Each expense records its category, amount, date, whether it is one-time or recurring, how it was paid and its payment status.',
    features: [
      'Summary of total, paid, pending and recurring expenses.',
      'Search and filter the expense ledger.',
      'Record a new expense or edit an existing one.',
      'View expense details and its supporting attachment.',
      'Mark an expense as paid.',
      'Print / download the expense voucher.',
      'Bulk delete selected expenses.',
    ],
    steps: [
      'Click Record Expense, choose a category (and vendor if applicable), enter the amount, date and payment details, then save.',
      'For repeat costs choose the recurring type and frequency (daily, weekly, monthly, quarterly, yearly).',
      'Use the search/filter controls to locate entries.',
      'Click a row to view details or the voucher actions to print/download.',
      'Use Pay to move a pending expense to paid.',
      'Select rows and Delete Selected to remove entries.',
    ],
    tips: [
      'Only PAID expenses contribute cash-out to the cashbook movement.',
      'Recurring expenses represent repeating obligations — review them when budgeting.',
      'Vendors are configured in the Vendors tab; expense categories live under the Categories tab.',
    ],
  },

  '/billing/assets/categories': {
    title: 'Asset Categories',
    subtitle: 'Group and classify fixed assets',
    overview:
      'Asset Categories group the fixed assets the hospital owns — medical equipment, furniture, IT equipment, vehicles, buildings and similar. The list is fully dynamic: add, rename or disable categories without a code change, and they appear immediately in the asset register and the Add/Edit Asset form.',
    features: [
      'Add, edit and delete asset categories.',
      'Enable/disable a category.',
      'Search and paginate the list.',
      'View category details.',
      'Bulk delete categories.',
    ],
    steps: [
      'Click Add Category, enter the name and optional description, then save.',
      'Use search or paging to find a category.',
      'Click a row to view details, the pencil to edit, or toggle active status.',
      'Select rows and Delete Selected to remove unused categories.',
    ],
    tips: [
      'Assets and expenses keep separate category lists; this one is for assets.',
      'A category used by an asset may not be safe to delete — disable it instead.',
    ],
  },

  '/billing/expenses/categories': {
    title: 'Expense Categories',
    subtitle: 'Group and classify operating expenses',
    overview:
      'Expense Categories group day-to-day running costs — rent, utilities, supplies, maintenance and similar. The list is fully dynamic and separate from asset categories, and it powers the category picker and filter on the expense ledger.',
    features: [
      'Add, edit and delete expense categories.',
      'Enable/disable a category.',
      'Search and paginate the list.',
      'View category details.',
      'Bulk delete categories.',
    ],
    steps: [
      'Click Add Category, enter the name and optional description, then save.',
      'Use search or paging to find a category.',
      'Click a row to view details, the pencil to edit, or toggle active status.',
      'Select rows and Delete Selected to remove unused categories.',
    ],
    tips: [
      'Expenses and assets keep separate category lists; this one is for expenses.',
      'Category names appear in the picker and filter on the Expenses page.',
    ],
  },

  '/billing/expenses/vendors': {
    title: 'Vendors',
    subtitle: 'Suppliers for expenses and assets',
    overview:
      'Vendors are the suppliers you buy from for both operating expenses and fixed assets. A vendor record holds contact and address details, tax information and payment terms that determine when invoices fall due.',
    features: [
      'Add a vendor via the form or Import CSV for bulk creation.',
      'View vendor details.',
      'Edit and delete vendors.',
      'Search and paginate the list.',
      'Bulk delete vendors.',
    ],
    steps: [
      'Click Add Vendor to create one vendor, or Import CSV to add many.',
      'Use search or paging to find a vendor.',
      'Click a row to view details, the pencil/Edit to update, or the trash icon to delete.',
      'Select rows and Delete Selected to remove several at once.',
    ],
    tips: [
      'Payment terms (days) set the expected due date on vendor invoices, which drives “overdue” payables.',
      'Vendors are used on both expense entries and the asset register.',
    ],
  },

  '/billing/expenses/vendors/form': {
    title: 'Vendor Details',
    subtitle: 'Create or update a supplier',
    overview:
      'This form creates a new vendor or updates an existing one. It captures identity, contact, location, tax and payment terms used across expenses and asset invoices.',
    features: [
      'Name, vendor code and contact person.',
      'Phone, email, location, city and address.',
      'Tax ID and payment terms (days).',
      'Notes and active/inactive flag.',
    ],
    steps: [
      'Fill in at least the vendor name and the contact details you have.',
      'Set payment terms in days if invoices should fall due on a schedule.',
      'Toggle Active off to stop using a vendor without deleting history.',
      'Click Save, or Back to return to the vendor list.',
    ],
    tips: [
      'Payment terms drive overdue calculations in payables reports.',
      'Inactive vendors remain visible on historic documents but are hidden from new-entry pickers.',
    ],
  },

  '/billing/expenses/form': {
    title: 'Expense Details',
    subtitle: 'Record or edit an expense',
    overview:
      'This form records a single operating expense or edits an existing one. It captures what was spent, the category, when, how it was paid and whether it repeats.',
    features: [
      'Title, category and amount.',
      'Expense date and one-time/recurring type with frequency.',
      'Payment status (e.g. PAID/PENDING), method and reference.',
      'Optional vendor and department attribution.',
      'Description, notes and an attachment for the receipt/invoice.',
    ],
    steps: [
      'Enter a title, choose a category and enter the amount.',
      'Set the expense date and pick One-time or Recurring (then choose the frequency).',
      'Choose the payment status, method and reference, and optionally a vendor/department.',
      'Attach the supporting receipt and add notes if needed.',
      'Click Save, or Back to discard and return to the ledger.',
    ],
    tips: [
      'Recurring expenses create a repeating obligation according to the frequency you select.',
      'Attachments keep the audit trail for the voucher print/download.',
      'Only expenses marked PAID count toward cash-out.',
    ],
  },

  /* ------------------------------------------------------------------ */
  /* HR — Payroll Policy                                                 */
  /* ------------------------------------------------------------------ */
  '/hr/payroll-policy': {
    title: 'Payroll Policy',
    subtitle: 'Rates, overtime and deduction rules for every pay run',
    overview:
      'Payroll Policy defines the rules used to calculate every salary: how the daily and hourly rate is derived, how overtime is paid and capped, which days are weekly off, and how absences and late arrivals reduce pay. A policy can apply to one department, act as the global default, or be assigned to a single employee as an override. When a pay run is generated, the system resolves the effective policy for each employee in this order: the employee’s own policy (if set), then their department policy, then the global default.',
    features: [
      'Create and manage multiple policies for different departments or staff groups.',
      'Mark one policy as the default fallback used when a department has no override.',
      'Assign an optional per-employee policy on the employee record that overrides the department and default policies.',
      'Configure the daily-rate basis: calendar days, fixed working days or actual working days.',
      'Set normal-day and holiday overtime multipliers, caps, grace and rounding rules.',
      'Choose weekly off days that stay fully paid.',
      'Define absence and late-penalty multipliers and a protected minimum net pay.',
      'Preview the effective policy for any department before a pay run.',
    ],
    steps: [
      'Click New Policy to open the policy builder.',
      'On the General tab, name the policy, choose a department (or leave it empty for the global default) and set the default/active flags and weekly off days.',
      'On the Pay Rates tab, choose the daily-rate basis, fixed working days and standard hours per day.',
      'On the Overtime tab, set the normal and holiday multipliers, caps, grace, rounding and minimum payable time.',
      'On the Deductions tab, set the absence and late-penalty multipliers, protected minimum net pay and allowance proration.',
      'Use the Summary panel to review the policy, then click Create Policy (or Save Changes).',
      'To override the policy for one person, open the employee (Add/Edit Employee → Allowances & Tax) and pick a Payroll Policy; leave it empty to inherit.',
      'Use the Effective policy card at the top of the page to preview what applies to a chosen department.',
    ],
    tips: [
      'Only one policy should be marked as the default fallback.',
      'Resolution order is employee policy → department policy → global default; the most specific one wins.',
      'A per-employee policy is ideal for the few staff who need different weekly off days or rates without their own department.',
      'Changes only affect future payroll runs; already-generated payslips are not recalculated.',
      'Set overtime caps to 0 to allow unlimited overtime.',
    ],
    bn: {
      title: 'পে-রোল নীতিমালা',
      subtitle: 'প্রতিটি বেতন হিসাবের হার, ওভারটাইম ও কর্তনের নিয়মাবলী',
      overview:
        'পে-রোল নীতিমালা প্রতিটি বেতন হিসাবের নিয়ম নির্ধারণ করে: দৈনিক ও ঘণ্টার হার কীভাবে নির্ধারিত হবে, ওভারটাইম কীভাবে পরিশোধ ও সীমিত হবে, কোন দিনগুলো সাপ্তাহিক বন্ধ, এবং অনুপস্থিতি ও দেরিতে আসার কারণে বেতন কীভাবে কমবে। একটি নীতিমালা একটি বিভাগে প্রযোজ্য হতে পারে, বৈশ্বিক ডিফল্ট হিসেবে থাকতে পারে, অথবা একজন কর্মীর জন্য আলাদা ওভাররাইড হিসেবে নির্ধারিত হতে পারে। বেতন তৈরি করার সময় সিস্টেম প্রতিটি কর্মীর জন্য এই ক্রমে কার্যকর নীতিমালা নির্ধারণ করে: কর্মীর নিজস্ব নীতিমালা (থাকলে), তারপর বিভাগের নীতিমালা, তারপর বৈশ্বিক ডিফল্ট।',
      features: [
        'বিভিন্ন বিভাগ বা কর্মী গোষ্ঠীর জন্য একাধিক নীতিমালা তৈরি ও পরিচালনা করুন।',
        'কোনো বিভাগের নির্দিষ্ট নীতিমালা না থাকলে যে ডিফল্ট ফলব্যাক ব্যবহৃত হবে তা নির্ধারণ করুন।',
        'কর্মীর রেকর্ডে ঐচ্ছিক আলাদা নীতিমালা নির্ধারণ করুন, যা বিভাগ ও ডিফল্ট নীতিমালাকে ওভাররাইড করে।',
        'দৈনিক হারের ভিত্তি নির্ধারণ করুন: ক্যালেন্ডার দিন, নির্দিষ্ট কর্মদিবস বা প্রকৃত কর্মদিবস।',
        'সাধারণ ও ছুটির দিনের ওভারটাইম গুণক, সীমা, গ্রেস ও রাউন্ডিং নিয়ম নির্ধারণ করুন।',
        'যে সাপ্তাহিক বন্ধ দিনগুলো সম্পূর্ণ পরিশোধিত থাকবে তা বাছাই করুন।',
        'অনুপস্থিতি ও দেরির জরিমানা গুণক এবং সুরক্ষিত ন্যূনতম নেট বেতন নির্ধারণ করুন।',
        'বেতন হিসাবের আগে যেকোনো বিভাগের কার্যকর নীতিমালা প্রিভিউ করুন।',
      ],
      steps: [
        'নতুন নীতিমালা তৈরি করতে “New Policy” ক্লিক করুন।',
        'General ট্যাবে নীতিমালার নাম দিন, বিভাগ নির্বাচন করুন (অথবা বৈশ্বিক ডিফল্টের জন্য খালি রাখুন) এবং ডিফল্ট/সক্রিয় সেটিং ও সাপ্তাহিক বন্ধ দিন নির্ধারণ করুন।',
        'Pay Rates ট্যাবে দৈনিক হারের ভিত্তি, নির্দিষ্ট কর্মদিবস ও দৈনিক আদর্শ ঘণ্টা নির্ধারণ করুন।',
        'Overtime ট্যাবে সাধারণ ও ছুটির গুণক, সীমা, গ্রেস, রাউন্ডিং ও ন্যূনতম প্রদেয় সময় নির্ধারণ করুন।',
        'Deductions ট্যাবে অনুপস্থিতি ও দেরির জরিমানা গুণক, সুরক্ষিত ন্যূনতম নেট বেতন ও ভাতার অনুপাত নির্ধারণ করুন।',
        'Summary প্যানেলে নীতিমালা পর্যালোচনা করুন, তারপর “Create Policy” (বা “Save Changes”) ক্লিক করুন।',
        'শুধু একজন কর্মীর নীতিমালা ওভাররাইড করতে তার প্রোফাইল খুলুন (Add/Edit Employee → Allowances & Tax) এবং একটি Payroll Policy বেছে নিন; ইনহেরিট করতে খালি রাখুন।',
        'পৃষ্ঠার উপরের “Effective policy” কার্ড ব্যবহার করে নির্বাচিত বিভাগে কী প্রযোজ্য হবে তা প্রিভিউ করুন।',
      ],
      tips: [
        'শুধুমাত্র একটি নীতিমালাকে ডিফল্ট ফলব্যাক হিসেবে চিহ্নিত করা উচিত।',
        'নির্ধারণের ক্রম: কর্মীর নীতিমালা → বিভাগের নীতিমালা → বৈশ্বিক ডিফল্ট; যেটি বেশি নির্দিষ্ট সেটিই প্রযোজ্য।',
        'আলাদা বিভাগ না বানিয়ে যেসব কর্মীর সাপ্তাহিক বন্ধ বা হার ভিন্ন, তাদের জন্য কর্মীভিত্তিক নীতিমালা আদর্শ।',
        'পরিবর্তন শুধুমাত্র ভবিষ্যতের পে-রোল হিসাবে কার্যকর হবে; ইতিমধ্যে তৈরি Payslip পুনরায় হিসাব হবে না।',
        'সীমাহীন ওভারটাইমের জন্য ওভারটাইম সীমা ০ দিন।',
      ],
    },
  },
};

/** Longest-prefix lookup so nested routes inherit their section’s help. */
export function getPageHelp(path: string): PageHelpContent | undefined {
  if (PAGE_HELP[path]) return PAGE_HELP[path];

  let bestKey: string | null = null;
  for (const key of Object.keys(PAGE_HELP)) {
    if (path === key || path.startsWith(`${key}/`)) {
      if (!bestKey || key.length > bestKey.length) bestKey = key;
    }
  }
  return bestKey ? PAGE_HELP[bestKey] : undefined;
}
